import { Button, Typography } from "components/atoms";
import { BackIcon } from "components/atoms/Icon";
import { useAppDispatch, useScreenSize } from "hooks";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DASHBOARD_PROGRAME, MCAT_TEST_TYPE, MOBILE_WIDTH, SELF_PACED_PROGRAME } from "utils/constants";
import { UpdateStatusDrillButtonSection } from "components/organisms/UpdateStatusDrillButtonSection";
import {
  homeApi,
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
  useUpdateStatusMutation,
} from "services/homeApi";
import { FetchError } from "components/molecules";
import { decodeToken } from "utils/functions/decodeToken";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import {
  replaceSelfPacedArrayOfObjects,
  reStructureSelfPacedSegment,
} from "utils/functions/selfPacedCourseWorkRestructure";
import { useSegmentReFetch } from "context";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import { useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

interface SelectedBtnState {
  [key: string]: boolean;
  NotStarted: boolean;
  InProgress: boolean;
  Complete: boolean;
}

export const DrillMobile = ({ theme }: IProps) => {
  const navigate = useNavigate();

  const screen = useScreenSize();

  const { state } = useLocation();

  const [selectedBtn, setSelectedBtn] = useState<SelectedBtnState>({
    NotStarted: true,
    InProgress: false,
    Complete: false,
  });

  const [performUpdate, setPerformUpdate] = useState(false);

  const {
    data: courseWorkData,
    setData,
    subject,
    page,
    selectedSession,
  } = useSegmentReFetch();

  const payload = decodeToken();

  const dispatch = useAppDispatch();

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const { refetch: refetchCoursework, isFetching: courseworkIsFetching } =
    useGetCourseworkQuery({
      page: page,
      ...(isDashboard
        ? {
            selectedOption: parseInt(
              getObjectKeysByValue(selectedBtn, true)[0],
              10
            ),
          }
        : {}),
      ...(isDashboard
        ? {
            timeFilter:
              selectedSession === null
                ? 1
                : getTimeFilterInput(Object.values(selectedSession)[0]),
          }
        : {}),
      ...(isSelfPaced
        ? {
            subject: subject,
          }
        : {}),
    });

  const { refetch: testScoresRefetch, isFetching: testScoresIsFetching } =
    useGetTestScoresAndPerformanceQuery();

  const [ triggerCourseIndexRefetch ] = useLazyGetCourseIndexQuery();

  const [ triggerStudyPlannerEventsRefetch, studyPlannerEventsRefetchResult ] =
    useLazyGetStudyPlannerEventsQuery();

  const { refetch: overAllProgressRefetch } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [
    triggerRefetchAiRecommendation
  ] = useLazyGetAiRecommendationsQuery();

  const handleBack = () => {
    setPerformUpdate(false);
    navigate(-1);
  };

  const deriveTitle = (type: any): string => {
    if (type === "OfflineReading") return "Reading Assignment";
    else return "Drill";
  };

  const [updateStatus, { isLoading, isError, error }]: any =
    useUpdateStatusMutation();

  const handleClick = async (data: any, status: any) => {
    setPerformUpdate(true);
    let extractStatus = Object.entries(status).find(
      (val: any) => val[1] === true
    );
    if (data !== undefined && status !== undefined && extractStatus) {
      try {
        await updateStatus({
          CoGuid:
            data.ContentObjectGuid || data.ContentObject.ContentObjectGuid,
          progress: extractStatus?.[0],
        }).unwrap();
        await handleRefreshStatus();
      } catch (error: any) {
        console.log("error : ", error);
      }
    }
  };

  const handleRefreshStatus = async () => {
    const promises = [
      refetchCoursework(),
      triggerCourseIndexRefetch(),
      overAllProgressRefetch(),
      testScoresRefetch(),
      triggerRefetchAiRecommendation(),
      ...(isDashboard ? [triggerStudyPlannerEventsRefetch()] : []),
    ];
    Promise.allSettled(promises).then((responses: any) => {
      if (responses[0].status === "fulfilled") {
        if (isDashboard) {
          const deStructuredArray = reStructureSelfPacedSegment(
            responses[0].value.data
          );
          const updatedArray = replaceSelfPacedArrayOfObjects(
            deStructuredArray,
            courseWorkData
          );
          setData([...updatedArray]);
        } else if (isSelfPaced) {
          const deStructuredArray = reStructureSelfPacedSegment(
            responses[0].value.data
          );
          const updatedArray = replaceSelfPacedArrayOfObjects(
            deStructuredArray,
            courseWorkData
          );
          setData([...updatedArray]);
        }
      }
    });
    dispatch(homeApi.util.invalidateTags(["Coursework"]));
  };

  useEffect(() => {
    if (screen.width > MOBILE_WIDTH) {
      setPerformUpdate(false);
      navigate("/");
    }
  }, [screen, navigate]);

  useEffect(() => {
    if (state && state.Status) {
      setSelectedBtn((prev: any) => {
        return {
          ...prev,
          InProgress:
            state.Status === "In Progress" || state.Status === "InProgress"
              ? true
              : false,
          NotStarted:
            state.Status === "Not Started" || state.Status === "Pending"
              ? true
              : false,
          Complete: state.Status === "Completed" ? true : false,
        };
      });
    }
  }, [state]);

  return (
    <div className="drill-mobile-wrapper">
      <div className="back-section">
        <BackIcon
          color={theme === "light" ? "#273E5A" : "#329EE2"}
          onClickHandle={handleBack}
        />
        <Typography label="Back" variant="h6Medium" onClick={handleBack} />
      </div>
      {state && (state.ContentObjectType || state.ContentObject) ? (
        <div className="title-wrapper">
          <Typography
            variant="h6Bold"
            label={deriveTitle(
              state.ContentObjectType || state.ContentObject.ContentObjectType
            )}
          />
        </div>
      ) : null}
      {state && (state.Name || state.name || state.Title) ? (
        <div className="content-wrapper">
          <Typography
            variant="h7Med"
            label={state.Name || state.name || state.Title}
          />
          <UpdateStatusDrillButtonSection
            selectedBtn={selectedBtn}
            setSelectedBtn={setSelectedBtn}
          />
        </div>
      ) : null}
      {state ? (
        <div className="button-wrapper">
          <Button
            icon={false}
            type="score"
            label="Done"
            onClick={() => handleClick(state, selectedBtn)}
            styles="button-custom-style"
            isLoading={
              isLoading ||
              courseworkIsFetching ||
              studyPlannerEventsRefetchResult.isFetching ||
              testScoresIsFetching
            }
          />
        </div>
      ) : null}
      {isError && performUpdate ? (
        <div className="error-wrapper">
          <FetchError content={`Update failed: ${error.data.Message}`} />
        </div>
      ) : null}
    </div>
  );
};
